import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, dateFr, FACTURE_STATUS } from "@/lib/constants";
import { generatePdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { FileDown, Eye, Copy } from "lucide-react";

const FILTERS = [
  { key: "", label: "Toutes" },
  { key: "brouillon", label: "Brouillons" },
  { key: "emise", label: "Émises" },
  { key: "partiellement_payee", label: "Partielles" },
  { key: "payee", label: "Payées" },
];

export default function FacturesList() {
  const [factures, setFactures] = useState([]);
  const [filter, setFilter] = useState("");
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async (status = "") => {
    const { data } = await api.get("/factures", { params: status ? { status } : {} });
    setFactures(data);
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const duplicate = async (id) => {
    try { const { data } = await api.post(`/factures/${id}/duplicate`); toast.success("Brouillon dupliqué"); navigate(`/factures/${data.id}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Factures</h1>
        <p className="text-slate-600">{factures.length} document(s) · brouillons, factures émises et avoirs</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((flt) => (
          <button
            key={flt.key}
            data-testid={`facture-filter-${flt.key || "all"}`}
            onClick={() => setFilter(flt.key)}
            className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
              filter === flt.key ? "bg-[#0284C7] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {flt.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {factures.length === 0 && <p className="text-slate-500">Aucune facture. Créez-en une en cliquant sur « Facturer » depuis un devis.</p>}
        {factures.map((f) => {
          const isAvoir = f.doc_type === "avoir";
          const isDraft = f.status === "brouillon" && !f.locked;
          return (
            <div key={f.id} data-testid={`facture-row-${f.id}`} className="bg-white border border-slate-200 rounded-md p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading font-bold text-lg text-slate-900">{f.number || "Brouillon"}</span>
                  {isAvoir && <span className="text-xs font-semibold px-2 py-1 rounded bg-red-50 text-red-600 border border-red-200">AVOIR</span>}
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${FACTURE_STATUS[f.status]?.cls}`}>{FACTURE_STATUS[f.status]?.label || f.status}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1 truncate">
                  {f.client?.name || "—"}
                  {isAvoir ? ` · sur ${f.facture_origine_number}` : (f.date_emission ? ` · Émise ${dateFr(f.date_emission)}` : ` · ${f.devis_number ? "Devis " + f.devis_number : "Non émise"}`)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-500">{isAvoir ? "Total avoir" : "Total TTC"}</div>
                <div className="font-heading font-extrabold text-xl text-[#0284C7]">{eur(f.total_ttc)}</div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button size="sm" variant="outline" data-testid={`open-facture-${f.id}`} onClick={() => navigate(`/factures/${f.id}`)} className="border-slate-300"><Eye size={16} className="mr-1" />Ouvrir</Button>
                <Button size="sm" variant="outline" data-testid={`pdf-facture-${f.id}`} onClick={() => generatePdf(f, settings, isAvoir ? "avoir" : "facture")} className="border-slate-300"><FileDown size={16} /></Button>
                {!isAvoir && <Button size="sm" variant="outline" data-testid={`dup-facture-${f.id}`} onClick={() => duplicate(f.id)} className="border-slate-300"><Copy size={16} /></Button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
